import { Spin, Tooltip } from 'antd';
import MaskedValueRenderer from 'components/MaskedValueRenderer';
import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

export interface PayrollSummaryTotals {
  headcount: number;
  incomeBeforeTax: number;
  contractGross: number;
  pit: number;
  insuranceEmployee: number;
  insuranceEmployer: number;
  advance: number;
  netPay: number;
  companyCost: number;
}

const PayrollSummaryCards: React.FC<{ totals: PayrollSummaryTotals | null; loading: boolean }> = ({
  totals,
  loading,
}) => {
  const { t } = useTranslation(['payroll']);
  return (
    <Spin spinning={loading}>
      <Grid>
        <Card $highlight>
          <Label>
            <Tooltip title={t('payroll:kpi_company_cost_hint')}>
              💰 {t('payroll:kpi_company_cost')}
            </Tooltip>
          </Label>
          <Value>
            <MaskedValueRenderer value={totals?.companyCost || 0} />
          </Value>
        </Card>
        <Card>
          <Label>💵 {t('payroll:kpi_gross')}</Label>
          <Value>
            <MaskedValueRenderer value={totals?.incomeBeforeTax || 0} />
          </Value>
        </Card>
        <Card>
          <Label>💳 {t('payroll:kpi_net')}</Label>
          <Value>
            <MaskedValueRenderer value={totals?.netPay || 0} />
          </Value>
        </Card>
        <Card>
          <Label>🏛️ {t('payroll:kpi_pit')}</Label>
          <Value>
            <MaskedValueRenderer value={totals?.pit || 0} />
          </Value>
        </Card>
        <Card>
          <Label>🏥 {t('payroll:kpi_insurance')}</Label>
          <Sub>
            {t('payroll:kpi_insurance_company')}:{' '}
            <MaskedValueRenderer value={totals?.insuranceEmployer || 0} />
          </Sub>
          <Sub>
            {t('payroll:kpi_insurance_employee')}:{' '}
            <MaskedValueRenderer value={totals?.insuranceEmployee || 0} />
          </Sub>
        </Card>
        <Card>
          <Label>👥 {t('payroll:kpi_headcount')}</Label>
          <Value>{totals?.headcount ?? 0}</Value>
        </Card>
      </Grid>
    </Spin>
  );
};

export default PayrollSummaryCards;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 12px;
  @media (max-width: 1400px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
`;
const Card = styled.div<{ $highlight?: boolean }>`
  background: ${(p) => (p.$highlight ? '#fff7ed' : '#fff')};
  border: 1px solid ${(p) => (p.$highlight ? '#fdba74' : '#f0f0f0')};
  border-radius: 8px;
  padding: 12px 14px;
`;
const Label = styled.div`
  color: #6b7280;
  font-size: 13px;
  margin-bottom: 6px;
`;
const Value = styled.div`
  font-size: 18px;
  font-weight: 600;
`;
const Sub = styled.div`
  font-size: 13px;
`;
